import { Box, ButtonBase, createStyles, makeStyles, Modal, Slide } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Colors, DynamicMuiIcon, Input, Typography } from 'src/components/atoms'
import { useUpdateDocumentFolder } from 'src/fixtures/modules/documentFolder/hooks'
import { DocumentFolder } from 'src/fixtures/types/folder'
import { ModalFolderDelete } from 'src/components/modals/ModalFolderDelete'

const MAX_FOLDER_NAME_LENGTH = 20

const useStyles = makeStyles(() => {
  return createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.base.black,
      zIndex: 9999
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '480px',
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid #DADCE0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#222B35'
    },
    closeButton: {
      width: '28px',
      height: '28px',
      border: 'none',
      background: 'transparent',
      color: '#707B89',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: '#E3E7ED'
      }
    },
    body: {
      padding: '32px 24px 40px'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#222B35',
      marginBottom: '8px'
    },
    requiredMark: {
      color: '#DE1B63',
      marginLeft: '4px'
    },
    helperText: {
      fontSize: '12px',
      color: '#707B89',
      marginTop: '6px'
    },
    footer: {
      padding: '16px 24px',
      borderTop: '1px solid #DADCE0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px'
    },
    footerWithDelete: {
      justifyContent: 'space-between'
    },
    footerRight: {
      display: 'flex',
      gap: '12px'
    }
  })
})

export interface ModalFolderEditProps {
  open: boolean
  folder: DocumentFolder | null
  onClose: () => void
  onSuccess?: () => void
}

export const ModalFolderEdit = ({ open, folder, onClose, onSuccess }: ModalFolderEditProps) => {
  const classes = useStyles()
  const [name, setName] = useState<string>('')
  const [touched, setTouched] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { handleUpdateFolder, isLoading: isUpdating } = useUpdateDocumentFolder()

  // モーダルを開くたびに状態をリセット
  useEffect(() => {
    if (open && folder) {
      setName(folder.name)
      setTouched(false)
      setShowDeleteConfirm(false)
    }
  }, [open, folder])

  // バリデーション
  const validationError = useMemo(() => {
    if (!name.trim()) {
      return 'フォルダ名を入力してください'
    }
    if (name.length > MAX_FOLDER_NAME_LENGTH) {
      return `フォルダ名は${MAX_FOLDER_NAME_LENGTH}文字以内で入力してください`
    }
    return null
  }, [name])

  const isValid = !validationError
  const hasChanges = folder && name.trim() !== folder.name

  const handleConfirm = useCallback(async () => {
    setTouched(true)
    if (!isValid || !folder || !hasChanges) return

    const result = await handleUpdateFolder(folder.id, { name: name.trim() })
    if (result) {
      onClose()
      onSuccess?.()
    }
  }, [name, isValid, folder, hasChanges, handleUpdateFolder, onClose, onSuccess])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }, [])

  const handleBlur = useCallback(() => {
    setTouched(true)
  }, [])

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true)
  }, [])

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  const handleDeleteSuccess = useCallback(() => {
    onClose()
    onSuccess?.()
  }, [onClose, onSuccess])

  if (!folder) return null

  // 削除確認表示
  if (showDeleteConfirm) {
    return (
      <ModalFolderDelete open={open} folder={folder} onClose={handleCloseDeleteModal} onSuccess={handleDeleteSuccess} />
    )
  }

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Slide in={open} direction="up">
        <Box className={classes.container}>
          {/* ヘッダー */}
          <Box className={classes.header}>
            <Typography className={classes.title}>フォルダを編集</Typography>
            <ButtonBase onClick={onClose} className={classes.closeButton}>
              <DynamicMuiIcon variant="close" size="20px" />
            </ButtonBase>
          </Box>

          {/* コンテンツ */}
          <Box className={classes.body}>
            <Box className={classes.label}>
              <span>フォルダ名</span>
              <span className={classes.requiredMark}>*</span>
            </Box>
            <Input
              name="folderName"
              fullWidth
              value={name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="フォルダ名を入力"
              error={touched && validationError ? validationError : undefined}
            />
            {(!touched || !validationError) && (
              <Typography className={classes.helperText}>最大{MAX_FOLDER_NAME_LENGTH}文字</Typography>
            )}
          </Box>

          {/* フッター */}
          <Box className={`${classes.footer} ${classes.footerWithDelete}`}>
            <Button kind="negative" variant="outlined" title="削除" onClick={handleDeleteClick} />
            <Box className={classes.footerRight}>
              <Button kind="neutral" variant="outlined" title="キャンセル" onClick={onClose} />
              <Button title="保存" onClick={handleConfirm} disabled={!isValid || !hasChanges} loading={isUpdating} />
            </Box>
          </Box>
        </Box>
      </Slide>
    </Modal>
  )
}
